<template>
    <div class="ui-page">
        <ui-page-header title="Etapas" subtitle="Cada etapa oferta um tipo de agrupamento com um mínimo sobre a avaliação, recebe os envelopes lacrados até o prazo e os abre em sessão pública.">
            <v-btn v-if="processId && $allowed('create')" color="primary" depressed @click="openForm(null)"><v-icon left small>mdi-plus</v-icon>Nova etapa</v-btn>
        </ui-page-header>

        <ui-table-card clearable :can-clear="!!search || !!status" @clear="search = ''; status = null">
            <template #toolbar>
                <competitive-process-picker v-model="processId" />
                <v-text-field v-model="search" placeholder="Nome ou descrição" dense outlined hide-details prepend-inner-icon="mdi-magnify" class="ui-toolbar__search"></v-text-field>
                <v-select v-model="status" :items="options.stage_statuses" placeholder="Todas as situações" clearable dense outlined hide-details class="ui-toolbar__field" :menu-props="{ offsetY: true }"></v-select>
            </template>
            <v-data-table :headers="headers" :items="visible" :loading="loading" disable-sort disable-pagination hide-default-footer
                :no-data-text="processId ? 'Nenhuma etapa cadastrada' : 'Selecione um processo'" loading-text="Carregando..." class="ui-clickable-rows" @click:row="openItems">
                <template v-slot:[`item.number`]="{ item }"><strong>{{ item.number }}</strong></template>
                <template v-slot:[`item.name`]="{ item }">
                    <div class="ui-cell-main">{{ item.name }}</div>
                    <div v-if="item.description" class="ui-cell-sub">{{ item.description }}</div>
                </template>
                <template v-slot:[`item.minimum_percent`]="{ item }"><span class="cp-num">{{ formatPercent(item.minimum_percent) }}</span></template>
                <template v-slot:[`item.status`]="{ item }"><ui-badge set="cp-stage" :status="item.status" /></template>
                <template v-slot:[`item.receiving`]="{ item }">
                    <div class="ui-nowrap ui-small">{{ formatSchedule(item.receiving_starts_at) }}</div>
                    <div class="ui-nowrap ui-small ui-muted">até {{ formatSchedule(item.receiving_ends_at) }}</div>
                </template>
                <template v-slot:[`item.opening_at`]="{ item }"><span class="ui-nowrap ui-small">{{ formatSchedule(item.opening_at) }}</span></template>
                <template v-slot:[`item.item_count`]="{ item }">
                    <span class="cp-num">{{ item.item_count }}</span>
                    <div v-if="item.blocked_items" class="ui-cell-sub cp-warn">{{ item.blocked_items }} {{ item.blocked_items === 1 ? "bloqueado" : "bloqueados" }}</div>
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                    <ui-row-actions :actions="[
                        { text: 'Itens da etapa', icon: 'mdi-format-list-checks', handler: () => openItems(item) },
                        { text: 'Editar etapa', icon: 'mdi-pencil-outline', handler: () => openForm(item), disabled: !$allowed('edit') },
                        { text: 'Excluir', icon: 'mdi-delete-outline', handler: () => remove(item), danger: true, disabled: !$allowed('delete') },
                    ]" />
                </template>
            </v-data-table>
        </ui-table-card>

        <!-- Cadastro da etapa -->
        <v-dialog v-model="form.open" max-width="640" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">{{ form.id ? `Etapa ${form.data.number}` : "Nova etapa" }}</h3>
                        <p class="ui-section-description">Horário de Brasília. O recebimento de uma etapa só começa depois da sessão da anterior.</p>
                    </div>
                </div>
                <div class="ui-card__body">
                    <v-row dense>
                        <v-col cols="3"><v-text-field v-model="form.data.number" type="number" min="1" label="Nº" outlined dense hide-details="auto" :placeholder="form.id ? '' : 'Auto'"></v-text-field></v-col>
                        <v-col cols="9"><v-text-field v-model="form.data.name" label="Nome" outlined dense hide-details="auto" maxlength="255" placeholder="Ex.: Lotes remanescentes · 1ª rodada"></v-text-field></v-col>
                        <v-col cols="12"><v-textarea v-model="form.data.description" label="Descrição (opcional)" outlined dense rows="2" auto-grow hide-details="auto"></v-textarea></v-col>
                        <v-col cols="7"><v-select v-model="form.data.offer_kind" :items="options.offer_kinds" label="Oferta" outlined dense hide-details="auto" :menu-props="{ offsetY: true }"></v-select></v-col>
                        <v-col cols="5"><v-text-field v-model="form.data.minimum_percent" label="Mínimo sobre a avaliação" suffix="%" outlined dense hide-details="auto"></v-text-field></v-col>
                        <v-col cols="6"><v-text-field v-model="form.data.receiving_starts_at" type="datetime-local" label="Início do recebimento" outlined dense hide-details="auto"></v-text-field></v-col>
                        <v-col cols="6"><v-text-field v-model="form.data.receiving_ends_at" type="datetime-local" label="Fim do recebimento" outlined dense hide-details="auto"></v-text-field></v-col>
                        <v-col cols="6"><v-text-field v-model="form.data.opening_at" type="datetime-local" label="Sessão pública de abertura" outlined dense hide-details="auto"></v-text-field></v-col>
                        <v-col cols="6"><v-select v-model="form.data.status" :items="options.stage_statuses" label="Situação" outlined dense hide-details="auto" :menu-props="{ offsetY: true }"></v-select></v-col>
                        <v-col cols="12" class="pb-0"><div class="ui-small ui-muted">Rodada de lances: itens com propostas válidas de dois ou mais proponentes, um de cada vez. Lance nos 2 minutos finais prorroga por mais 2.</div></v-col>
                        <v-col cols="6"><v-text-field v-model="form.data.bid_round_at" type="datetime-local" label="Início da rodada (opcional)" hint="Vazio: 10 minutos após a abertura" persistent-hint outlined dense hide-details="auto"></v-text-field></v-col>
                        <v-col cols="3"><v-text-field v-model="form.data.bid_increment" label="Incremento" prefix="R$" hint="Vazio: 1%" persistent-hint outlined dense hide-details="auto"></v-text-field></v-col>
                        <v-col cols="3"><v-text-field v-model="form.data.bid_duration_minutes" type="number" min="1" max="60" label="Duração" suffix="min" outlined dense hide-details="auto"></v-text-field></v-col>
                        <v-col cols="6"><v-text-field v-model="form.data.final_term_minutes" type="number" min="5" max="1440" label="Prazo do termo de proposta final" suffix="min" hint="Para o vencedor assinar com o A1 (R10)" persistent-hint outlined dense hide-details="auto"></v-text-field></v-col>
                    </v-row>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined :disabled="form.saving" @click="form.open = false">Cancelar</v-btn>
                    <v-btn color="primary" depressed :loading="form.saving" :disabled="!String(form.data.name || '').trim() || !form.data.offer_kind" @click="saveForm">Salvar</v-btn>
                </div>
            </v-card>
        </v-dialog>

        <v-dialog v-model="block.open" max-width="480" persistent>
            <v-card v-if="block.item" class="ui-card">
                <div class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">Bloquear {{ block.item.code }}</h3>
                        <p class="ui-section-description">O item continua listado na etapa, mas não recebe proposta.</p>
                    </div>
                </div>
                <div class="ui-card__body">
                    <v-textarea v-model="block.reason" label="Motivo do bloqueio" outlined dense rows="2" auto-grow hide-details="auto" maxlength="500" autofocus></v-textarea>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined :disabled="block.saving" @click="block.open = false">Cancelar</v-btn>
                    <v-btn color="primary" depressed :loading="block.saving" :disabled="!block.reason.trim()" @click="saveBlock(block.item, true, block.reason)">Bloquear</v-btn>
                </div>
            </v-card>
        </v-dialog>

        <!-- Itens da etapa -->
        <ui-sheet v-model="sheet.open" :width="780">
            <template #header>
                <div v-if="sheet.stage" class="cp-card__slug">Etapa {{ sheet.stage.number }} · {{ sheet.stage.offer_kind_label }} · mínimo {{ formatPercent(sheet.stage.minimum_percent) }}</div>
                <h3 class="ui-section-title">{{ sheet.stage ? sheet.stage.name : "Carregando..." }}</h3>
                <p v-if="sheet.stage" class="ui-section-description">
                    {{ sheet.stage.item_count }} {{ sheet.stage.item_count === 1 ? "item" : "itens" }} · avaliação {{ formatMoney(sheet.stage.appraisal_total) }} · mínimo somado {{ formatMoney(sheet.stage.minimum_total) }}
                </p>
            </template>

            <div v-if="sheet.loading" class="ui-empty"><v-progress-circular indeterminate size="28" color="primary"></v-progress-circular></div>
            <template v-else-if="sheet.stage && !sheet.picking">
                <div class="d-flex align-center mt-3 mb-2">
                    <div class="ui-label mb-0">Itens ofertados</div>
                    <v-spacer></v-spacer>
                    <v-btn v-if="$allowed('edit')" small outlined @click="startPicking"><v-icon left small>mdi-playlist-edit</v-icon>Escolher itens</v-btn>
                </div>
                <v-data-table :headers="itemHeaders" :items="sheet.stage.items" disable-sort disable-pagination hide-default-footer dense no-data-text="Nenhum item nesta etapa">
                    <template v-slot:[`item.code`]="{ item }"><span class="ui-mono">{{ item.code }}</span></template>
                    <template v-slot:[`item.name`]="{ item }">
                        <div class="ui-cell-main">{{ item.name }}</div>
                        <div class="ui-cell-sub">
                            <span v-if="item.type !== 'asset'">{{ item.asset_count }} {{ item.asset_count === 1 ? "ativo" : "ativos" }}</span><span v-else>{{ item.location || "sem município" }}</span>
                            <span v-if="item.unavailable_assets" class="cp-warn"> · {{ item.unavailable_assets }} {{ item.unavailable_assets === 1 ? "indisponível" : "indisponíveis" }}</span>
                        </div>
                        <div v-if="item.blocked" class="ui-cell-sub cp-warn">Bloqueado: {{ item.block_reason }}</div>
                    </template>
                    <template v-slot:[`item.appraisal_value`]="{ item }"><span class="cp-num">{{ formatMoney(item.appraisal_value) }}</span></template>
                    <template v-slot:[`item.minimum_value`]="{ item }"><span class="cp-num" :class="{ 'ui-muted': item.blocked }">{{ formatMoney(item.minimum_value) }}</span></template>
                    <template v-slot:[`item.actions`]="{ item }">
                        <v-btn v-if="$allowed('edit')" x-small text @click="toggleBlock(item)">{{ item.blocked ? "Desbloquear" : "Bloquear" }}</v-btn>
                    </template>
                </v-data-table>
            </template>
            <template v-else-if="sheet.stage">
                <div class="d-flex align-center flex-wrap mt-3 mb-2" style="gap: 12px;">
                    <div class="ui-label mb-0">{{ sheet.stage.offer_kind_label }} disponíveis</div>
                    <span class="ui-small ui-muted">{{ sheet.selected.length }} de {{ sheet.candidates.length }} {{ sheet.selected.length === 1 ? "selecionado" : "selecionados" }}</span>
                    <v-spacer></v-spacer>
                    <v-btn x-small text @click="sheet.selected = sheet.candidates.map((item) => item.ref_id)">Todos</v-btn>
                    <v-btn x-small text @click="sheet.selected = []">Nenhum</v-btn>
                </div>
                <v-text-field v-model="sheet.search" placeholder="Buscar" dense outlined hide-details prepend-inner-icon="mdi-magnify" class="mb-2"></v-text-field>
                <div class="cp-pick-list">
                    <div v-for="item in pickable" :key="item.ref_id" class="cp-pick">
                        <v-checkbox :input-value="sheet.selected.includes(item.ref_id)" dense hide-details @change="togglePick(item.ref_id)"></v-checkbox>
                        <div class="cp-pick__text">
                            <div class="ui-cell-main"><span class="ui-mono">{{ item.code }}</span> · {{ item.name }}</div>
                            <div class="ui-cell-sub">
                                <span v-if="item.asset_count !== undefined">{{ item.asset_count }} {{ item.asset_count === 1 ? "ativo" : "ativos" }}</span><span v-else>{{ item.location || "sem município" }}</span>
                                <span v-if="item.status === 'blocked'" class="cp-warn"> · ativo bloqueado</span>
                            </div>
                        </div>
                        <div class="cp-pick__value">{{ formatMoney(item.appraisal_value) }}<div class="ui-cell-sub">mín. {{ formatMoney(item.minimum_value) }}</div></div>
                    </div>
                    <div v-if="!pickable.length" class="ui-empty">Nada para ofertar. Cadastre {{ sheet.stage.offer_kind === "asset" ? "ativos" : "os agrupamentos em Cestas e lotes" }}.</div>
                </div>
            </template>

            <template #footer>
                <template v-if="sheet.picking">
                    <v-spacer></v-spacer>
                    <v-btn outlined :disabled="sheet.saving" @click="sheet.picking = false">Cancelar</v-btn>
                    <v-btn color="primary" depressed :loading="sheet.saving" @click="savePicking">Salvar itens</v-btn>
                </template>
                <template v-else>
                    <v-btn v-if="sheet.stage && $allowed('edit')" text @click="openForm(sheet.stage)"><v-icon left small>mdi-pencil-outline</v-icon>Editar etapa</v-btn>
                    <v-spacer></v-spacer>
                    <v-btn outlined @click="sheet.open = false">Fechar</v-btn>
                </template>
            </template>
        </ui-sheet>
    </div>
</template>

<script>
import axios from "axios";
import CompetitiveProcessPicker from "@/components/competitive/CompetitiveProcessPicker.vue";
import processScope from "@/components/competitive/process-scope";
import { API, formatMoney, formatPercent, formatSchedule, loadOptions, notifyError, notifySuccess, toInputDateTime } from "@/components/competitive/competitive.util";

export default {
    name: "CompetitiveStageList",
    components: { CompetitiveProcessPicker },
    mixins: [processScope],
    data: () => ({
        options: { offer_kinds: [], stage_statuses: [] },
        items: [],
        loading: false,
        search: "",
        status: null,
        form: { open: false, id: null, saving: false, data: {} },
        block: { open: false, item: null, reason: "", saving: false },
        sheet: { open: false, loading: false, saving: false, picking: false, stage: null, candidates: [], selected: [], search: "" },
        headers: [
            { text: "Nº", value: "number", width: 56 },
            { text: "Etapa", value: "name" },
            { text: "Oferta", value: "offer_kind_label", width: 140 },
            { text: "Mínimo", value: "minimum_percent", width: 90 },
            { text: "Situação", value: "status", width: 130 },
            { text: "Recebimento", value: "receiving", width: 170 },
            { text: "Sessão de abertura", value: "opening_at", width: 160 },
            { text: "Itens", value: "item_count", width: 110 },
            { text: "", value: "actions", align: "end", width: 48 },
        ],
        itemHeaders: [
            { text: "Código", value: "code", width: 80 },
            { text: "Item", value: "name" },
            { text: "Avaliação", value: "appraisal_value", width: 150 },
            { text: "Mínimo", value: "minimum_value", width: 150 },
            { text: "", value: "actions", align: "end", width: 96 },
        ],
    }),
    computed: {
        visible() {
            const term = this.search.trim().toLowerCase();
            return this.items.filter((item) => (!this.status || item.status === this.status)
                && (!term || [item.name, item.description].some((value) => String(value || "").toLowerCase().includes(term))));
        },
        pickable() {
            const term = this.sheet.search.trim().toLowerCase();
            return this.sheet.candidates.filter((item) => !term || [item.code, item.name, item.location].some((value) => String(value || "").toLowerCase().includes(term)));
        },
    },
    async created() {
        try {
            this.options = await loadOptions();
        } catch (error) {
            notifyError(this, error);
        }
    },
    methods: {
        formatMoney,
        formatPercent,
        formatSchedule,
        onProcess() {
            this.load();
        },
        async load() {
            if (!this.processId) return;
            this.loading = true;
            try {
                const result = await axios.get(`${API}/processes/${this.processId}/stages`);
                this.items = result.data.content;
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.loading = false;
            }
        },
        openForm(stage) {
            this.form = {
                open: true,
                id: stage ? stage.id : null,
                saving: false,
                data: stage
                    ? {
                        number: stage.number,
                        name: stage.name,
                        description: stage.description || "",
                        offer_kind: stage.offer_kind,
                        minimum_percent: String(stage.minimum_percent).replace(".", ","),
                        receiving_starts_at: toInputDateTime(stage.receiving_starts_at),
                        receiving_ends_at: toInputDateTime(stage.receiving_ends_at),
                        opening_at: toInputDateTime(stage.opening_at),
                        status: stage.status,
                        bid_round_at: toInputDateTime(stage.bid_round_at),
                        bid_increment: stage.bid_increment === null || stage.bid_increment === undefined ? "" : String(stage.bid_increment).replace(".", ","),
                        bid_duration_minutes: stage.bid_duration_minutes || 5,
                        final_term_minutes: stage.final_term_minutes || 30,
                    }
                    : { number: "", name: "", description: "", offer_kind: null, minimum_percent: "", receiving_starts_at: "", receiving_ends_at: "", opening_at: "", status: "planned", bid_round_at: "", bid_increment: "", bid_duration_minutes: 5, final_term_minutes: 30 },
            };
        },
        async saveForm() {
            this.form.saving = true;
            try {
                const data = { ...this.form.data };
                ["receiving_starts_at", "receiving_ends_at", "opening_at", "bid_round_at"].forEach((key) => { data[key] = data[key] || null; });
                if (data.number === "") delete data.number;
                const result = this.form.id
                    ? await axios.patch(`${API}/stages/${this.form.id}`, data)
                    : await axios.post(`${API}/processes/${this.processId}/stages`, data);
                notifySuccess(this, "Etapa salva.");
                this.form.open = false;
                if (this.sheet.open && this.sheet.stage && this.sheet.stage.id === result.data.content.id) this.sheet.stage = result.data.content;
                await this.load();
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.form.saving = false;
            }
        },
        async remove(stage) {
            const confirmed = await this.$dialog.confirm({
                title: "Excluir etapa",
                text: `A etapa ${stage.number} (${stage.name}) e a lista de itens dela saem do processo. Confirma?`,
                actions: { false: "Cancelar", true: { text: "Excluir", color: "error" } },
            });
            if (!confirmed) return;
            try {
                await axios.delete(`${API}/stages/${stage.id}`);
                await this.load();
            } catch (error) {
                notifyError(this, error);
            }
        },
        async openItems(stage) {
            this.sheet = { open: true, loading: true, saving: false, picking: false, stage: null, candidates: [], selected: [], search: "" };
            try {
                const result = await axios.get(`${API}/stages/${stage.id}`);
                this.sheet.stage = result.data.content;
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.sheet.loading = false;
            }
        },
        async startPicking() {
            this.sheet.loading = true;
            try {
                const result = await axios.get(`${API}/stages/${this.sheet.stage.id}/candidates`);
                this.sheet.candidates = result.data.content;
                this.sheet.selected = result.data.content.filter((item) => item.selected).map((item) => item.ref_id);
                this.sheet.search = "";
                this.sheet.picking = true;
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.sheet.loading = false;
            }
        },
        togglePick(id) {
            const index = this.sheet.selected.indexOf(id);
            if (index >= 0) this.sheet.selected.splice(index, 1);
            else this.sheet.selected.push(id);
        },
        async savePicking() {
            this.sheet.saving = true;
            try {
                const result = await axios.put(`${API}/stages/${this.sheet.stage.id}/items`, { ref_ids: this.sheet.selected });
                this.sheet.stage = result.data.content;
                this.sheet.picking = false;
                notifySuccess(this, "Itens da etapa salvos.");
                await this.load();
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.sheet.saving = false;
            }
        },
        toggleBlock(item) {
            if (item.blocked) return this.saveBlock(item, false, null);
            this.block = { open: true, item, reason: "", saving: false };
        },
        async saveBlock(item, blocked, reason) {
            this.block.saving = true;
            try {
                const result = await axios.patch(`${API}/stage-items/${item.id}`, { blocked, block_reason: reason });
                this.sheet.stage = result.data.content;
                this.block.open = false;
                await this.load();
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.block.saving = false;
            }
        },
    },
};
</script>

<style lang="scss">
@import "../../components/competitive/competitive";
</style>
